
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, Settings, UserRound, LayoutDashboard, ChartBar } from "lucide-react";
import { useEffect, useState } from "react";
import { LoginDialog } from "./LoginDialog";
import { SignUpDialog } from "./SignUpDialog";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

export const AuthButton = () => {
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const checkSession = async () => {
    console.log("check");
    
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error checking session:', error);
      return;
    }
    setIsAuthenticated(!!session);
    if (session?.user?.id && !isAuthenticated) {
      fetchProfile(session.user.id);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("username, avatar_url")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }
      setUsername(data?.username || null);
      setAvatarUrl(data?.avatar_url || null);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out");
    }
  };

  if (isAuthenticated) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar className="h-10 w-10">
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} alt={username || "User avatar"} />
              ) : (
                <AvatarFallback>
                  {username ? username[0].toUpperCase() : <User className="h-6 w-6" />}
                </AvatarFallback>
              )}
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate("/dashboard")}>
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Dashboard
          </DropdownMenuItem>
          {isMobile && (
            <DropdownMenuItem onClick={() => navigate("/dashboard/analytics")}>
              <ChartBar className="mr-2 h-4 w-4" />
              Analytics
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => username && navigate(`/${username.toLowerCase()}`)}>
            <UserRound className="mr-2 h-4 w-4" />
            My Public Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/profile")}>
            <Settings className="mr-2 h-4 w-4" />
            Profile Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => setIsLoginOpen(true)}>
          Log in
        </Button>
        <Button onClick={() => setIsSignUpOpen(true)}>Get Started</Button>
      </div>
      <LoginDialog open={isLoginOpen} onOpenChange={setIsLoginOpen} />
      <SignUpDialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen} />
    </>
  );
};
